import type { Document } from "../document/Document";
import type { Command } from "./Command";
import { setPos } from "./MoveLayerCommand";

/** A single layer's absolute from/to move, one entry per affected layer. */
export interface LayerMoveEntry {
  readonly layerId: string;
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

// MoveLayersCommand — move N layers to their own absolute positions in ONE
// atomic, undoable step.
//
// Used by MoveTool when the Move tool drags while 2+ layers are selected in
// the Layers panel (or the active layer has linked layers): Photoshop moves
// every selected/linked layer by the same pixel delta as one gesture, and a
// single Ctrl+Z undoes all of it (audit i4, related i56).
//
// For a single layer MoveTool keeps emitting the existing MoveLayerCommand
// (kind "move-layer") unchanged — this command is only used once there is at
// least one "extra" layer beyond the primary being dragged, so existing
// single-layer behaviour and tests are untouched.
export class MoveLayersCommand implements Command {
  readonly kind = "move-layers";

  constructor(private readonly moves: readonly LayerMoveEntry[]) {}

  apply(doc: Document): Document {
    let layers = doc.layers;
    for (const m of this.moves) {
      layers = setPos(layers, m.layerId, m.toX, m.toY);
    }
    return { ...doc, layers };
  }

  invert(_before: Document, _after: Document): Command {
    return new MoveLayersCommand(
      this.moves.map((m) => ({
        layerId: m.layerId,
        fromX: m.toX,
        fromY: m.toY,
        toX: m.fromX,
        toY: m.fromY,
      })),
    );
  }
}
